/**
 * ListeningExercises — replaces the generic Material upload flow for the
 * "Hören / Escuchar" tile under Üben.
 *
 * Design (per user spec):
 *   - Teacher uploads an audio file + question + multiple-choice options + the
 *     correct answer + CEFR levels.
 *   - Stored in `db.exercises` with `exercise_type: 'listening'`, status
 *     `approved`. This means the SAME exercise automatically shows up in
 *     Spiele → Hörverstehen, classified by level — no duplication required.
 *   - Students see the audio player + options + immediate feedback (+1 point).
 *
 * Both views read from `GET /api/exercises?exercise_type=listening&level=...`.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Headphones, Plus, X, Spinner, Check, GraduationCap, Trash, CheckCircle, XCircle, Sparkle, Square, Pencil, PaperPlaneTilt,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import NewBadge from './NewBadge';
import ExerciseEditModal from './ExerciseEditModal';
import { scheduleBadgeText } from '../lib/scheduleDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

// ── Single exercise card (audio + MC, immediate feedback) ────────────────────
function ListeningCard({ ex, user, onAttempted, onDeleted, onUpdated, groups = [], onResend }) {
  const [answer, setAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [audioSrc, setAudioSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  // Fetch audio bytes lazily — exercises list omits them to keep the payload small.
  const loadAudio = async () => {
    if (audioSrc) return;
    try {
      const res = await axios.get(`${API}/exercises/${ex.exercise_id}/audio`, { withCredentials: true });
      const b64 = res.data?.audio_base64;
      if (b64) setAudioSrc(`data:audio/mp3;base64,${b64}`);
    } catch {
      toast.error('Audio konnte nicht geladen werden');
    }
  };

  useEffect(() => { loadAudio(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ex.exercise_id]);

  const submit = async () => {
    if (answer == null || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/exercises/submit`,
        { exercise_id: ex.exercise_id, answer: ex.options[answer] },
        { withCredentials: true });
      setResult(res.data);
      if (res.data.is_correct) toast.success('Richtig! +1 Punkt');
      onAttempted?.();
    } catch {
      toast.error('Fehler');
    } finally {
      setBusy(false);
    }
  };

  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';

  const removeExercise = async () => {
    if (!window.confirm('Diese Hörverstehen-Übung in den Material-Papierkorb verschieben?')) return;
    try {
      await axios.delete(`${API}/exercises/${ex.exercise_id}`, { withCredentials: true });
      toast.success('Übung in den Papierkorb verschoben — wiederherstellbar im Lehrer-Dashboard');
      onDeleted?.(ex.exercise_id);
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3" data-testid={`listening-card-${ex.exercise_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {((ex.levels && ex.levels.length > 0) ? ex.levels : [ex.level]).map(l => (
            <span key={l} className={`level-badge ${l}`}>{l}</span>
          ))}
          <span className="text-xs uppercase tracking-wider text-[#78716C]">Hörverstehen</span>
          <NewBadge createdAt={ex.created_at} authorRole="teacher" />
          {ex.last_result === 'correct' && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
              <CheckCircle size={11} weight="fill" /> Bereits gemeistert
            </span>
          )}
          {ex.last_result === 'incorrect' && (
            <span className="inline-flex items-center gap-1 text-xs text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full">
              <XCircle size={11} weight="fill" /> Nochmal versuchen
            </span>
          )}
        </div>
        {ex.title && <span className="text-sm font-medium text-[#57534E] truncate flex-1">{ex.title}</span>}
        {isTeacher && (
          <>
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500 hover:text-amber-700 flex-shrink-0"
              title="Übung bearbeiten"
              data-testid={`listening-edit-${ex.exercise_id}`}
            >
              <Pencil size={14} weight="bold" />
            </button>
            <button
              onClick={() => onResend?.(ex)}
              className="p-1.5 rounded-lg hover:bg-violet-50 text-violet-500 hover:text-violet-700 flex-shrink-0"
              title="An andere Klassen zuweisen"
              data-testid={`listening-resend-${ex.exercise_id}`}
            >
              <PaperPlaneTilt size={14} weight="duotone" />
            </button>
            {ex.scheduled_at && new Date(ex.scheduled_at) > new Date() && (
              <span
                className="text-[10px] font-bold tracking-wider uppercase bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full"
                data-testid={`listening-sched-${ex.exercise_id}`}
                title={`Wird veröffentlicht: ${new Date(ex.scheduled_at).toLocaleString('de-DE')}`}
              >
                {scheduleBadgeText(ex.scheduled_at)}
              </span>
            )}
            <button
              onClick={removeExercise}
              className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0"
              title="In Material-Papierkorb verschieben"
              data-testid={`listening-delete-${ex.exercise_id}`}
            >
              <Trash size={14} />
            </button>
          </>
        )}
      </div>

      {audioSrc
        ? <audio controls preload="metadata" src={audioSrc} className="w-full max-w-md h-9 rounded-full" style={{maxHeight:'36px'}} data-testid={`listening-audio-${ex.exercise_id}`} />
        : <div className="h-10 flex items-center justify-center text-xs text-[#A8A29E]"><Spinner size={14} className="animate-spin mr-2" /> Lade Audio…</div>}

      <h3 className="font-heading text-base font-semibold text-[#292524]">{ex.question}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {(ex.options || []).map((opt, i) => {
          const selected = answer === i;
          const isCorrect = result && result.correct_answer === opt;
          const isWrongPick = result && selected && !result.is_correct;
          return (
            <button key={i} disabled={!!result} onClick={() => setAnswer(i)}
              className={`p-3 rounded-xl text-left text-sm font-medium border-2 transition-all ${
                isCorrect ? 'bg-green-100 border-green-400 text-green-800'
                : isWrongPick ? 'bg-red-100 border-red-400 text-red-800'
                : selected ? 'border-amber-400 bg-amber-50'
                : 'border-stone-200 bg-white hover:border-amber-300'
              }`}
              data-testid={`listening-opt-${ex.exercise_id}-${i}`}>
              {opt}
              {isCorrect && <Check size={14} className="inline ml-2" weight="bold" />}
              {isWrongPick && <X size={14} className="inline ml-2" weight="bold" />}
            </button>
          );
        })}
      </div>

      {!result ? (
        <Button onClick={submit} disabled={answer == null || busy}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid={`listening-submit-${ex.exercise_id}`}>
          {busy ? <Spinner size={14} className="animate-spin mr-2" /> : <Check size={14} className="mr-2" weight="bold" />}
          Prüfen
        </Button>
      ) : result.explanation && (
        <p className="text-sm text-[#57534E] bg-stone-50 p-3 rounded-lg border border-stone-200">{result.explanation}</p>
      )}

      {isTeacher && (
        <p className="text-[10px] text-[#A8A29E] pt-1 border-t border-stone-100">
          {ex.created_at ? new Date(ex.created_at).toLocaleDateString('de-DE') : ''}
          {ex.author_name && ` · von ${ex.author_name}`}
          {ex.source === 'teacher_upload' && ' · eigenes Audio'}
        </p>
      )}
      <ExerciseEditModal
        exercise={ex}
        open={editing}
        onClose={() => setEditing(false)}
        onSaved={(updated) => onUpdated?.(updated)}
      />
    </div>
  );
}

// ── Teacher upload form ──────────────────────────────────────────────────────
function TeacherUploadForm({ onCreated, groups = [] }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [correctIdx, setCorrectIdx] = useState(0);
  const [levels, setLevels] = useState(['A2']);
  const [groupId, setGroupId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  // Audio is held as a raw File until the form is submitted — no upload step
  // is required from the user beyond picking the file. Avoids the "click Enviar
  // first" footgun that the AudioRecorder workflow had.
  const [audioFile, setAudioFile] = useState(null);
  const [recording, setRecording] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const [busy, setBusy] = useState(false);

  const toggleLvl = (l) => setLevels(ls => ls.includes(l) ? ls.filter(x => x !== l) : [...ls, l]);
  const setOpt = (i, v) => setOptions(prev => prev.map((o, idx) => idx === i ? v : o));
  const addOpt = () => options.length < 6 && setOptions(prev => [...prev, '']);
  const rmOpt = (i) => {
    if (options.length <= 2) return;
    setOptions(prev => prev.filter((_, idx) => idx !== i));
    if (correctIdx >= options.length - 1) setCorrectIdx(0);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast.error('Audio zu groß (max 50 MB)'); return; }
    setAudioFile(f);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        // Wrap blob as a File so we can append it to FormData with a sensible name.
        const file = new File([blob], `aufnahme_${Date.now()}.webm`, { type: blob.type });
        setAudioFile(file);
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error('Mikrofon konnte nicht gestartet werden');
    }
  };
  const stopRecording = () => { try { mediaRecRef.current?.stop(); } catch { /* noop */ } };

  const reset = () => {
    setTitle(''); setQuestion(''); setOptions(['', '', '', '']); setCorrectIdx(0);
    setLevels(['A2']); setAudioFile(null); setOpen(false);
    setGroupId(''); setScheduledAt('');
  };

  const submit = async () => {
    const cleanOpts = options.map(o => o.trim()).filter(Boolean);
    if (!title.trim()) return toast.error('Titel erforderlich');
    if (!question.trim()) return toast.error('Frage erforderlich');
    if (cleanOpts.length < 2) return toast.error('Mindestens 2 Antwortoptionen');
    if (correctIdx >= cleanOpts.length) return toast.error('Richtige Antwort auswählen');
    if (!audioFile) return toast.error('Audio aufnehmen oder hochladen');
    if (!levels.length) return toast.error('Mindestens ein Niveau wählen');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', audioFile, audioFile.name || 'audio.mp3');
      fd.append('title', title.trim());
      fd.append('question', question.trim());
      fd.append('levels', JSON.stringify(levels));
      fd.append('level', levels[0]);
      fd.append('options', JSON.stringify(cleanOpts));
      fd.append('correct_answer', cleanOpts[correctIdx]);
      // Optional group scoping + scheduled publish. Empty strings clear both
      // (visible to all classes, publish immediately).
      if (groupId.trim()) fd.append('group_id', groupId.trim());
      if (scheduledAt.trim()) {
        // datetime-local is local time; convert to UTC ISO so the schedule
        // matches what the teacher intends regardless of server timezone.
        try {
          fd.append('scheduled_at', new Date(scheduledAt).toISOString());
        } catch { /* noop — bad input silently dropped */ }
      }
      await axios.post(`${API}/teacher/exercises/audio-upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Hörverstehen-Übung veröffentlicht — auch in „Spiele → Hörverstehen" sichtbar');
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Veröffentlichen');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)}
        className="bg-[#2F6B50] hover:bg-[#245140] text-white"
        data-testid="listening-add-open">
        <Plus size={16} weight="bold" className="mr-2" />
        Hörverstehen-Übung anlegen
      </Button>
    );
  }

  return (
    <div className="border-2 border-[#2F6B50]/30 bg-[#F3F9F5] rounded-2xl p-5 space-y-4" data-testid="listening-add-form">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-[#292524] flex items-center gap-2">
          <GraduationCap size={20} weight="duotone" className="text-[#2F6B50]" /> Neue Hörverstehen-Übung
        </h3>
        <button onClick={reset} className="text-[#78716C] hover:text-[#292524]" disabled={busy}><X size={18} /></button>
      </div>

      <div>
        <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Titel</label>
        <Input value={title} onChange={e => setTitle(e.target.value)}
          placeholder="z.B. Dialog im Café"
          className="mt-1 bg-white"
          data-testid="listening-add-title" />
      </div>

      <div>
        <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Frage</label>
        <Input value={question} onChange={e => setQuestion(e.target.value)}
          placeholder="z.B. Was bestellt die Frau?"
          className="mt-1 bg-white"
          data-testid="listening-add-question" />
      </div>

      <div>
        <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">
          Antwortoptionen <span className="text-[#A8A29E] normal-case">— wähle die richtige Antwort</span>
        </label>
        <div className="space-y-2 mt-1.5">
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCorrectIdx(i)}
                className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors ${
                  correctIdx === i ? 'bg-green-500 border-green-500 text-white' : 'border-stone-300 bg-white text-stone-400 hover:border-green-400'
                }`}
                title="Als richtige Antwort markieren"
                data-testid={`listening-add-correct-${i}`}
              >
                {correctIdx === i ? <Check size={14} weight="bold" /> : i + 1}
              </button>
              <Input value={o} onChange={e => setOpt(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
                className="flex-1 bg-white"
                data-testid={`listening-add-opt-${i}`} />
              {options.length > 2 && (
                <button onClick={() => rmOpt(i)} className="text-red-400 hover:text-red-600 p-1" type="button">
                  <Trash size={14} />
                </button>
              )}
            </div>
          ))}
          {options.length < 6 && (
            <button onClick={addOpt} type="button"
              className="text-xs font-semibold text-[#2F6B50] hover:underline inline-flex items-center gap-1 mt-1">
              <Plus size={12} weight="bold" /> Weitere Option
            </button>
          )}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Audio</label>
        <div className="mt-1.5 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {!recording ? (
              <Button
                type="button"
                onClick={startRecording}
                variant="outline"
                className="bg-white"
                disabled={busy}
                data-testid="listening-add-record-start">
                <Headphones size={14} weight="duotone" className="mr-2" />
                Aufnehmen
              </Button>
            ) : (
              <Button
                type="button"
                onClick={stopRecording}
                className="bg-red-500 hover:bg-red-600 text-white animate-pulse"
                data-testid="listening-add-record-stop">
                <Square size={14} weight="fill" className="mr-2" />
                Aufnahme stoppen
              </Button>
            )}
            <span className="text-xs text-[#A8A29E]">oder</span>
            <label className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 text-sm">
              <Plus size={14} weight="bold" />
              <span>Datei wählen</span>
              <input
                type="file"
                accept="audio/*,video/mp4,.mp3,.wav,.m4a,.mp4,.aac,.ogg,.webm,.flac"
                className="hidden"
                onChange={onPickFile}
                disabled={busy || recording}
                data-testid="listening-add-file-input"
              />
            </label>
            {audioFile && (
              <span className="text-xs text-[#2F6B50] font-medium inline-flex items-center gap-1.5">
                <Check size={12} weight="bold" />
                {audioFile.name} ({(audioFile.size / 1024).toFixed(0)} KB)
                <button
                  type="button"
                  onClick={() => setAudioFile(null)}
                  className="text-red-500 hover:text-red-700 ml-1"
                  disabled={busy}
                  title="Entfernen">
                  <X size={12} weight="bold" />
                </button>
              </span>
            )}
          </div>
          {audioFile && (
            <audio
              controls
              preload="metadata"
              src={URL.createObjectURL(audioFile)}
              className="w-full max-w-md h-9 rounded-full mt-2"
              style={{maxHeight:'36px'}}
              data-testid="listening-add-audio-preview"
            />
          )}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Niveaus</label>
        <div className="flex gap-1.5 mt-1.5 flex-wrap">
          {LEVELS.map(l => {
            const on = levels.includes(l);
            return (
              <button key={l} type="button" onClick={() => toggleLvl(l)}
                className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 ${
                  on ? 'bg-[#2F6B50] text-white border-[#2F6B50]' : 'bg-white text-[#57534E] border-stone-300'
                }`}
                data-testid={`listening-add-lvl-${l}`}>
                {l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Optional group scoping + scheduled publish (mirrors MaterialsPage).
          Empty group + empty schedule = visible to everyone immediately. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Klasse (optional)</label>
          <select
            value={groupId}
            onChange={e => setGroupId(e.target.value)}
            className="mt-1 w-full text-sm border border-stone-300 rounded-md px-2 py-2 bg-white"
            data-testid="listening-add-group"
          >
            <option value="">— Alle Klassen —</option>
            {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.name} · {g.level}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Veröffentlichen am (optional)</label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={e => setScheduledAt(e.target.value)}
            className="mt-1 w-full text-sm border border-stone-300 rounded-md px-2 py-2 bg-white"
            data-testid="listening-add-scheduled-at"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-stone-200">
        <Button variant="outline" onClick={reset} disabled={busy}>Abbrechen</Button>
        <Button onClick={submit} disabled={busy}
          className="bg-[#AB1C24] hover:bg-[#8A151C] text-white"
          data-testid="listening-add-submit">
          {busy ? <Spinner size={14} className="animate-spin mr-2" /> : <Sparkle size={14} className="mr-2" weight="fill" />}
          {busy ? 'Wird gespeichert…' : 'Veröffentlichen'}
        </Button>
      </div>
    </div>
  );
}

// ── Main section ────────────────────────────────────────────────────────────
export default function ListeningExercises({ user, lang }) {
  const isDe = lang !== 'es';
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  // Students always see only their own level. Teachers can browse all levels
  // because they need to QA every level's exercise pool.
  const [filterLevel, setFilterLevel] = useState(isTeacher ? 'all' : (user?.spanish_level || 'A2'));
  const [groups, setGroups] = useState([]);
  const [resendEx, setResendEx] = useState(null);
  const [resendSelected, setResendSelected] = useState(new Set());
  const [resendBusy, setResendBusy] = useState(false);

  const toggleResendTarget = (gid) => setResendSelected(prev => {
    const next = new Set(prev);
    if (next.has(gid)) next.delete(gid); else next.add(gid);
    return next;
  });
  useEffect(() => { setResendSelected(new Set()); }, [resendEx]);

  const submitResend = async () => {
    if (!resendEx) return;
    const targets = Array.from(resendSelected);
    if (targets.length === 0) return toast.error('Bitte mindestens eine Klasse auswählen');
    setResendBusy(true);
    try {
      await axios.post(`${API}/exercises/${resendEx.exercise_id}/resend`,
        { group_ids: targets }, { withCredentials: true });
      toast.success(`An ${targets.length} Klasse${targets.length === 1 ? '' : 'n'} zugewiesen`);
      setResendEx(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setResendBusy(false); }
  };

  useEffect(() => {
    if (!isTeacher) return;
    axios.get(`${API}/groups`, { withCredentials: true })
      .then(r => setGroups(r.data || []))
      .catch(() => setGroups([]));
  }, [isTeacher]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { exercise_type: 'listening', limit: 100 };
      if (filterLevel && filterLevel !== 'all') params.level = filterLevel;
      const res = await axios.get(`${API}/exercises`, { params, withCredentials: true });
      setExercises(res.data || []);
    } catch {
      toast.error(isDe ? 'Fehler beim Laden' : 'Error al cargar');
    } finally {
      setLoading(false);
    }
  }, [filterLevel, isDe]);

  useEffect(() => { load(); }, [load]);

  // Group by level for a Spiele-like layout (visible to teachers + when the
  // student filter is "all", which doesn't happen normally — kept for symmetry).
  const byLevel = exercises.reduce((acc, ex) => {
    const lvls = (ex.levels && ex.levels.length) ? ex.levels : [ex.level || 'A2'];
    for (const l of lvls) {
      (acc[l] ||= []).push(ex);
    }
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="listening-section">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
            <Headphones size={28} weight="duotone" className="text-[#D97706]" />
            {isDe ? 'Hören' : 'Escuchar'}
          </h1>
          <p className="text-sm text-[#57534E] mt-1">
            {isTeacher
              ? (isDe
                  ? 'Lade ein Audio hoch und gib Antwortoptionen an. Die Übung erscheint automatisch auch unter Spiele → Hörverstehen für das passende Niveau.'
                  : 'Sube un audio y añade opciones. El ejercicio aparecerá automáticamente también en Juegos → Comprensión auditiva en el nivel correspondiente.')
              : (isDe
                  ? 'Höre die Audios und wähle die richtige Antwort. Jede Übung gibt dir +1 Punkt.'
                  : 'Escucha los audios y elige la respuesta correcta. Cada ejercicio te da +1 punto.')
            }
          </p>
        </div>
      </header>

      {isTeacher && <TeacherUploadForm onCreated={load} groups={groups} />}

      {/* Level filter (teachers only) */}
      {isTeacher && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="listening-level-filter">
          {['all', ...LEVELS].map(l => (
            <button key={l}
              onClick={() => setFilterLevel(l)}
              className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 ${
                filterLevel === l ? 'bg-[#292524] text-white border-[#292524]' : 'bg-white text-[#57534E] border-stone-200 hover:border-stone-400'
              }`}>
              {l === 'all' ? (isDe ? 'Alle' : 'Todos') : l}
            </button>
          ))}
          <span className="text-xs text-[#A8A29E] ml-2">{exercises.length} {exercises.length === 1 ? 'Übung' : 'Übungen'}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Spinner size={32} className="animate-spin text-[#AB1C24]" /></div>
      ) : exercises.length === 0 ? (
        <p className="text-center text-[#78716C] py-10 bg-white rounded-xl border border-stone-200">
          {isTeacher
            ? (isDe ? 'Noch keine Hörverstehen-Übungen. Lege oben die erste an.' : 'Aún no hay ejercicios de comprensión auditiva.')
            : (isDe ? 'Noch keine Übungen auf deinem Niveau. Schau bald wieder vorbei.' : 'Aún no hay ejercicios en tu nivel. Vuelve pronto.')}
        </p>
      ) : isTeacher && filterLevel === 'all' ? (
        // Teacher overview by level
        <div className="space-y-5">
          {LEVELS.map(l => (byLevel[l] || []).length > 0 && (
            <section key={l} data-testid={`listening-level-section-${l}`}>
              <h2 className="font-heading text-lg font-bold text-[#292524] mb-3 flex items-center gap-2">
                <span className={`level-badge ${l}`}>{l}</span>
                <span>{byLevel[l].length} {byLevel[l].length === 1 ? 'Übung' : 'Übungen'}</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {byLevel[l].map(ex => <ListeningCard key={ex.exercise_id} ex={ex} user={user} onAttempted={load} onDeleted={load} onUpdated={load} groups={groups} onResend={setResendEx} />)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {exercises.map(ex => <ListeningCard key={ex.exercise_id} ex={ex} user={user} onAttempted={load} onDeleted={load} onUpdated={load} groups={groups} onResend={setResendEx} />)}
        </div>
      )}

      {/* Teacher-only: resend a Hörverstehen exercise to other classes. */}
      {resendEx && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !resendBusy && setResendEx(null)} data-testid="listening-resend-dialog">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold text-lg text-[#292524]">An weitere Klassen zuweisen</h3>
                <p className="text-sm text-[#78716C] mt-1 truncate">„{resendEx.title || resendEx.question || '(ohne Titel)'}"</p>
              </div>
              <button onClick={() => !resendBusy && setResendEx(null)} className="p-1 rounded-lg hover:bg-stone-100"><X size={18} /></button>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => toggleResendTarget('__ALL__')}
                className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${resendSelected.has('__ALL__') ? 'border-[#AB1C24] bg-[#AB1C24]/5' : 'border-stone-200 hover:border-stone-400'}`}
                data-testid="listening-resend-target-__ALL__"
              >
                <span className="font-semibold text-[#292524]">Alle Klassen</span>
                <span className="ml-2 text-xs text-[#78716C]">— Für jeden Schüler sichtbar</span>
              </button>
              {groups.map(g => (
                <button
                  key={g.group_id}
                  type="button"
                  onClick={() => toggleResendTarget(g.group_id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${resendSelected.has(g.group_id) ? 'border-[#AB1C24] bg-[#AB1C24]/5' : 'border-stone-200 hover:border-stone-400'}`}
                  data-testid={`listening-resend-target-${g.group_id}`}
                >
                  <span className="font-semibold text-[#292524]">{g.name}</span>
                  {g.level && <span className="ml-2 text-xs text-[#78716C]">· {g.level}</span>}
                </button>
              ))}
              {groups.length === 0 && (
                <p className="text-xs text-[#A8A29E] italic px-1">Keine Klassen definiert.</p>
              )}
            </div>
            <div className="flex gap-2 pt-2 border-t border-stone-200">
              <Button variant="outline" onClick={() => setResendEx(null)} disabled={resendBusy} className="flex-1" data-testid="listening-resend-cancel">Abbrechen</Button>
              <Button
                onClick={submitResend}
                disabled={resendBusy || resendSelected.size === 0}
                className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white"
                data-testid="listening-resend-submit"
              >
                {resendBusy ? <Spinner size={14} className="animate-spin mr-2" /> : <PaperPlaneTilt size={14} className="mr-2" weight="fill" />}
                {resendBusy ? 'Sende…' : `An ${resendSelected.size} Klasse${resendSelected.size === 1 ? '' : 'n'} senden`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
